import { Exercise as ComponentExercise, ExerciseType, PronunciationPracticeData } from "@/services/admin/exercises.interfaces";
import { EditorInterface } from "./EditorInterface";
import { Mic } from "lucide-react";
import { ExerciseType as InternalExerciseType } from "./types";

/**
 * Editor para exercícios de gravação de áudio (versão unificada)
 * Utiliza a mesma estrutura de dados que PronunciationPractice com foco na gravação geral
 */
export class AudioRecordingInfo extends EditorInterface {
  /**
   * Retorna as informações para exibir no cartão do exercício de gravação de áudio
   * @param exercise Objeto do exercício
   */
  static GetCardInfo(exercise: ComponentExercise): {
    title: string;
    subtitle: string;
    description?: string;
  } {
    if (!exercise || !AudioRecordingInfo.SupportsExerciseType(exercise.type as unknown as string)) {
      return {
        title: 'Gravação de Áudio',
        subtitle: 'Sem detalhes',
      };
    }

    try {
      // Tenta extrair os dados do exercício, lidando com diferentes formatos
      let question = 'Sem instruções para gravação';
      let duration = 'Sem limite';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as PronunciationPracticeData;
        question = data.question || data.instructions || 'Sem instruções para gravação';
        duration = data.maxDuration ? `${data.maxDuration}s` : 'Sem limite';
      } 
      // Se os dados estiverem no formato plano
      else if ('question' in exercise || 'instructions' in exercise) {
        question = (exercise as any).question || (exercise as any).instructions || 'Sem instruções para gravação';
        duration = (exercise as any).maxDuration ? `${(exercise as any).maxDuration}s` : 'Sem limite';
      }
      
      return {
        title: "Gravação de Áudio",
        subtitle: question.length > 60 ? `${question.substring(0, 60)}...` : question,
        description: `Duração: ${duration}`,
      };
    } catch (error) {
      console.error("Erro ao processar dados do exercício de gravação de áudio:", error);
      return {
        title: 'Gravação de Áudio',
        subtitle: 'Erro ao processar dados',
      };
    }
  }
  
  /**
   * Retorna o título do tipo de exercício de gravação de áudio
   */
  static getTitle(): string {
    return "Gravação de Áudio";
  }
  
  /**
   * Retorna uma descrição formatada para o exercício de gravação de áudio
   * @param exercise Objeto do exercício
   */
  static getDescription(exercise: ComponentExercise): string {
    if (!exercise) {
      return "Sem detalhes";
    }
    
    try {
      // Tenta extrair os dados do exercício, lidando com diferentes formatos
      let duration = 'Sem limite';
      
      // Se os dados estiverem no formato aninhado (data é um objeto)
      if (exercise.data && typeof exercise.data === 'object') {
        const data = exercise.data as PronunciationPracticeData;
        duration = data.maxDuration ? `${data.maxDuration}s` : 'Sem limite';
      } 
      // Se os dados estiverem no formato plano
      else if ('maxDuration' in exercise) {
        duration = (exercise as any).maxDuration ? `${(exercise as any).maxDuration}s` : 'Sem limite';
      }
      
      return `Duração: ${duration}`;
    } catch (error) {
      console.error("Erro ao processar dados do exercício:", error);
      return "Erro ao processar dados";
    }
  }
  
  /**
   * Retorna o ícone para exercícios de gravação de áudio
   */
  static getIcon() {
    return Mic;
  }
  
  /**
   * Verifica se o exercício é do tipo de gravação de áudio
   */
  static SupportsExerciseType(type: string): boolean {
    return type === ExerciseType.AudioRecording || type === InternalExerciseType.AudioRecording;
  }
}